import React, { useState, useCallback, useMemo, useEffect } from 'react';
import { ChevronDown, ChevronUp } from 'lucide-react';
import SolidColorPicker from './SolidColorPicker';
import GradientPicker from './GradientPicker';
import ColorPresets, { addToRecentColors } from './ColorPresets';
import { parseColorValue, colorValueToCss, DEFAULT_GRADIENT } from './colorUtils';

/**
 * ColorPicker - Main color picker component combining solid and gradient pickers
 *
 * @param {Object} props
 * @param {any} props.value - Current color value (string, object, or null)
 * @param {Function} props.onChange - Callback when color changes (value: object) => void
 * @param {boolean} props.allowGradient - Show gradient tab (default: false)
 * @param {boolean} props.allowTransparent - Show transparent button (default: true)
 * @param {Array} props.savedColors - Saved page colors
 * @param {Function} props.onSaveColor - Save color to page (value: object, label: string) => void
 * @param {Function} props.onDeleteSavedColor - Delete saved color (id: string) => void
 */
export default function ColorPicker({
  value,
  onChange,
  allowGradient = false,
  allowTransparent = true,
  savedColors = [],
  onSaveColor,
  onDeleteSavedColor,
}) {
  // Parse the incoming value into normalized format
  const parsedValue = useMemo(() => parseColorValue(value), [value]);

  // Determine initial mode based on parsed value
  const getInitialMode = useCallback(() => {
    if (parsedValue.type === 'linear-gradient' || parsedValue.type === 'radial-gradient') {
      return 'gradient';
    }
    return 'solid';
  }, [parsedValue.type]);

  // Current mode: 'solid' or 'gradient'
  const [mode, setMode] = useState(getInitialMode);

  // State for presets visibility (collapsed by default)
  const [showPresets, setShowPresets] = useState(false);

  // Update mode when value type changes externally
  useEffect(() => {
    const newMode = getInitialMode();
    if (newMode !== mode) {
      setMode(newMode);
    }
  }, [getInitialMode]); // eslint-disable-line react-hooks/exhaustive-deps

  // Checkered pattern for transparency backgrounds
  const checkeredBg = `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='16' height='16'%3E%3Crect x='0' y='0' width='8' height='8' fill='%23ccc'/%3E%3Crect x='8' y='8' width='8' height='8' fill='%23ccc'/%3E%3Crect x='8' y='0' width='8' height='8' fill='%23fff'/%3E%3Crect x='0' y='8' width='8' height='8' fill='%23fff'/%3E%3C/svg%3E")`;

  // Get CSS representation for preview
  const cssValue = useMemo(() => colorValueToCss(parsedValue), [parsedValue]);

  // Check if current color is transparent
  const isTransparent = parsedValue.type === 'solid' && parsedValue.alpha === 0;

  // Handle solid color change from SolidColorPicker
  const handleSolidChange = useCallback(
    (color, alpha) => {
      const newValue = {
        type: 'solid',
        color,
        alpha,
      };
      onChange(newValue);
      addToRecentColors(newValue);
    },
    [onChange]
  );

  // Handle gradient change from GradientPicker
  const handleGradientChange = useCallback(
    (gradientValue) => {
      onChange(gradientValue);
      addToRecentColors(gradientValue);
    },
    [onChange]
  );

  // Handle preset color selection
  const handlePresetSelect = useCallback(
    (colorValue) => {
      onChange(colorValue);
      // addToRecentColors is called inside ColorPresets

      // Switch mode if needed
      if (colorValue.type === 'solid') {
        setMode('solid');
      } else if (colorValue.type === 'linear-gradient' || colorValue.type === 'radial-gradient') {
        setMode('gradient');
      }
    },
    [onChange]
  );

  // Handle transparent button click
  const handleTransparent = useCallback(() => {
    const newValue = {
      type: 'solid',
      color: parsedValue.type === 'solid' ? parsedValue.color : '#000000',
      alpha: 0,
    };
    onChange(newValue);
    addToRecentColors(newValue);
    setMode('solid');
  }, [onChange, parsedValue]);

  // Handle mode switch
  const handleModeSwitch = useCallback(
    (newMode) => {
      if (newMode === mode) return;

      setMode(newMode);

      if (newMode === 'solid') {
        // Convert gradient to solid (use first stop color)
        if (parsedValue.type === 'linear-gradient' || parsedValue.type === 'radial-gradient') {
          const firstStop = parsedValue.stops?.[0];
          const newValue = {
            type: 'solid',
            color: firstStop?.color || '#000000',
            alpha: firstStop?.alpha ?? 100,
          };
          onChange(newValue);
        }
      } else if (newMode === 'gradient') {
        // Convert solid to gradient (use current color as first stop)
        if (parsedValue.type === 'solid') {
          const newValue = {
            type: 'linear-gradient',
            angle: 90,
            stops: [
              { color: parsedValue.color, alpha: parsedValue.alpha, position: 0 },
              { color: '#FFFFFF', alpha: 100, position: 100 },
            ],
          };
          onChange(newValue);
        } else {
          // Already a gradient, just keep it
          onChange(parsedValue);
        }
      }
    },
    [mode, parsedValue, onChange]
  );

  // Get current color/alpha for solid picker
  const solidColor = parsedValue.type === 'solid' ? parsedValue.color : '#000000';
  const solidAlpha = parsedValue.type === 'solid' ? parsedValue.alpha : 100;

  // Get current gradient value for gradient picker
  const gradientValue = useMemo(() => {
    if (parsedValue.type === 'linear-gradient' || parsedValue.type === 'radial-gradient') {
      return parsedValue;
    }
    return DEFAULT_GRADIENT;
  }, [parsedValue]);

  return (
    <div className="w-full space-y-3">
      {/* Mode Tabs (only show if gradients allowed) */}
      {allowGradient && (
        <div className="flex p-1 bg-gray-100 rounded-lg">
          <button
            type="button"
            onClick={() => handleModeSwitch('solid')}
            className={`flex-1 px-3 py-1.5 text-sm font-medium rounded-md transition-all ${
              mode === 'solid'
                ? 'bg-white text-gray-900 shadow-sm'
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            Solid
          </button>
          <button
            type="button"
            onClick={() => handleModeSwitch('gradient')}
            className={`flex-1 px-3 py-1.5 text-sm font-medium rounded-md transition-all ${
              mode === 'gradient'
                ? 'bg-white text-gray-900 shadow-sm'
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            Gradient
          </button>
        </div>
      )}

      {/* Compact preview row: swatch + css value + clear button */}
      <div className="flex items-center gap-3">
        <div
          className="w-8 h-8 rounded border border-gray-200 flex-shrink-0 overflow-hidden"
          style={{
            backgroundImage: checkeredBg,
            backgroundColor: '#fff',
          }}
        >
          <div
            className="w-full h-full"
            style={{
              background: cssValue,
            }}
          />
        </div>
        <p
          className="flex-1 text-xs text-gray-500 font-mono truncate"
          title={cssValue}
        >
          {cssValue}
        </p>
        {allowTransparent && (
          <button
            type="button"
            onClick={handleTransparent}
            className={`px-2 py-1 text-xs font-medium rounded transition-colors flex-shrink-0 ${
              isTransparent
                ? 'bg-gray-200 text-gray-700'
                : 'text-gray-500 hover:bg-gray-100 hover:text-gray-700'
            }`}
          >
            {isTransparent ? 'Transparent' : 'Clear'}
          </button>
        )}
      </div>

      {/* Color Picker Content */}
      <div>
        {mode === 'solid' ? (
          <SolidColorPicker
            color={solidColor}
            alpha={solidAlpha}
            onChange={handleSolidChange}
          />
        ) : (
          <GradientPicker
            value={gradientValue}
            onChange={handleGradientChange}
          />
        )}
      </div>

      {/* Presets toggle button */}
      <button
        type="button"
        onClick={() => setShowPresets(!showPresets)}
        className="flex items-center gap-1 text-sm text-gray-600 hover:text-gray-800 transition-colors"
      >
        {showPresets ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
        {showPresets ? 'Hide Presets' : 'Show Presets'}
      </button>

      {/* Collapsible presets */}
      {showPresets && (
        <>
          <div className="border-t border-gray-200" />
          <ColorPresets
            onSelectColor={handlePresetSelect}
            currentColor={parsedValue}
            savedColors={savedColors}
            onSaveColor={onSaveColor}
            onDeleteSavedColor={onDeleteSavedColor}
          />
        </>
      )}
    </div>
  );
}
